'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import { ArrowLeft, Save, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/lib/toast';
import {
  createRole,
  getPermissionsGrouped,
  type PermissionGroup,
  type Permission
} from '@/services/api/organization';
import { ApiClientError } from '@/lib/api-client';

export default function NewRolePage() {
  const router = useRouter();
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const data = await getPermissionsGrouped();
      setPermissionGroups(data);
    } catch (error) {
      console.error('Failed to load permissions:', error);
      toast.error(t.organization.roles.loadPermissionsFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePermission = (permissionId: string) => {
    const newSelected = new Set(selectedPermissions);
    if (newSelected.has(permissionId)) {
      newSelected.delete(permissionId);
    } else {
      newSelected.add(permissionId);
    }
    setSelectedPermissions(newSelected);
  };

  const handleToggleResourcePermissions = (resourcePermissions: Permission[]) => {
    const allSelected = resourcePermissions.every(p => selectedPermissions.has(p.id));
    const newSelected = new Set(selectedPermissions);
    
    resourcePermissions.forEach(p => {
      if (allSelected) {
        newSelected.delete(p.id);
      } else {
        newSelected.add(p.id);
      }
    });
    
    setSelectedPermissions(newSelected);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error(t.organization.roles.nameRequired);
      return;
    }

    if (!code.trim()) {
      toast.error(t.organization.roles.codeRequired);
      return;
    }

    try {
      setSaving(true);
      const newRole = await createRole({
        name: name.trim(),
        code: code.trim(),
        description: description.trim() || undefined,
        permissionIds: Array.from(selectedPermissions),
      });
      
      toast.success(t.organization.roles.createSuccess);
      router.push(`/organization/roles/system-roles/${newRole.id}`);
    } catch (error) {
      console.error('Failed to create role:', error);
      if (error instanceof ApiClientError) {
        toast.error(`${t.common.createFailed}: ${error.message}`);
      } else {
        toast.error(t.organization.roles.createFailed);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-full bg-gray-50">
      <div className="px-8 py-8">
        {/* 页面头部 */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => router.push('/organization/roles/system-roles')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.common.backToList}
          </Button>
          
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-600" />
            <h1 className="text-2xl font-semibold text-gray-900">{t.organization.roles.createRole}</h1>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl">
          {/* 基本信息 */}
          <Card>
            <CardHeader>
              <CardTitle>{t.organization.basicInfo}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">{t.organization.roleName} *</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t.organization.roles.namePlaceholder}
                    required
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t.organization.roles.nameHint}
                  </p>
                </div>

                <div>
                  <Label htmlFor="code">{t.organization.roleCode} *</Label>
                  <Input
                    id="code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder={t.organization.roles.codePlaceholder}
                    required
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t.organization.roles.codeHint}
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="description">{t.organization.roleDescription}</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t.organization.roles.descriptionPlaceholder}
                  rows={3}
                  className="mt-1"
                />
              </div>
            </CardContent>
          </Card>

          {/* 权限配置 */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t.organization.roles.permissionsConfig}</CardTitle>
                <div className="text-sm text-gray-600">
                  {t.organization.roles.selectedCount.replace('{count}', selectedPermissions.size.toString())}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-gray-500">{t.organization.roles.loadingPermissions}</div>
              ) : (
                <div className="space-y-4">
                  {permissionGroups.map((group) => {
                    const groupPermissions = group.permissions;
                    const selectedCount = groupPermissions.filter(p => 
                      selectedPermissions.has(p.id)
                    ).length;
                    const allSelected = selectedCount === groupPermissions.length;

                    return (
                      <div key={group.resource} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={allSelected}
                              onChange={() => handleToggleResourcePermissions(groupPermissions)}
                              className="w-4 h-4 rounded border-gray-300"
                            />
                            <h3 className="font-medium text-gray-900">
                              {group.resourceName || group.resource}
                            </h3>
                            {group.module && (
                              <Badge variant="outline" className="text-xs">
                                {group.module}
                              </Badge>
                            )}
                          </div>
                          <span className="text-sm text-gray-500">
                            {selectedCount}/{groupPermissions.length}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-6">
                          {groupPermissions.map((permission) => (
                            <label
                              key={permission.id}
                              className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedPermissions.has(permission.id)}
                                onChange={() => handleTogglePermission(permission.id)}
                                className="w-4 h-4 rounded border-gray-300"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900">
                                  {permission.action}
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                  {permission.description}
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* 操作按钮 */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => router.push('/organization/roles/system-roles')}
              disabled={saving}
            >
              取消
            </Button>
            <Button type="submit" disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? t.common.creating : t.organization.roles.createRole}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

